import { Mutex } from "async-mutex";
import { LocalParticipant as TwilioLocalParticipant } from "twilio-video";
import { logger } from "../../../datadog/logger";
import { AudioVideoMediaKind } from "../../core/room/participant/media/types";
import { LocalAVTrackContextAtom, LocalMediaStatus } from "../../core/types";
import { TrackOperationResult } from "./types";
import { releaseTrack, unpublishLocalTrack } from "./utils";

const trackKindToMutexMap = {
  audio: new Mutex(),
  video: new Mutex(),
};

/**
 * Unpublishes the local track (audio or video) and optionally releases/stops it
 *
 * @param trackKind - The type of track ("audio" or "video")
 * @param localTrack - The local track atom to unpublish
 * @param shouldReleaseTrack - Whether to stop/release the track completely
 * @param localParticipant - Optional Twilio local participant to unpublish from
 * @returns The unpublished track or undefined
 */
export async function unpublishLocalAudioVideoTrack(
  trackKind: AudioVideoMediaKind,
  localAVTrackAtom: LocalAVTrackContextAtom,
  shouldReleaseTrack: boolean = false,
  localParticipant?: TwilioLocalParticipant,
): Promise<TrackOperationResult> {
  const release = await trackKindToMutexMap[trackKind]?.acquire();
  const { track: currentTrack } = localAVTrackAtom;

  if (!currentTrack) {
    // Return existing state
    return { success: false, ...localAVTrackAtom };
  }

  try {
    // Unpublish the track using the utility function
    unpublishLocalTrack(currentTrack, localParticipant);

    if (shouldReleaseTrack) {
      // Release/stop the track completely
      releaseTrack(currentTrack);
      return {
        success: true,
        status: LocalMediaStatus.DISABLED,
        track: undefined,
      };
    } else {
      // Keep the track but mark it as disabled
      return {
        success: true,
        status: LocalMediaStatus.DISABLED,
        track: currentTrack,
      };
    }
  } catch (error) {
    logger.error(
      `Unable to unpublish local ${trackKind} track`,
      {},
      error as Error,
    );
    // Restore previous state on error
    return { success: false, ...localAVTrackAtom };
  } finally {
    release?.();
  }
}
